import React, { useContext, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useParams } from "react-router-dom";

import { Coupon } from "@safetyculture/s12-apis-connect-web/s12/subscriptions/v1/coupons_pb";
import { ActionType } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/action_pb";

import SubscriptionAction from "./subscription-action";
import { convertTrial, validateCoupon } from "api/endpoints/subscription";
import { callOptionsHeaders } from "api/helpers";
import ConfirmDialog from "components/shared/confirm-dialog";
import { DEFAULT_SNACKBAR_DURATION, EventTypeReason } from "constants/index";
import { OrgDataContext } from "context/organisation-context";
import { PermissionsContext } from "context/permissions-context";
import useDebouncedEffect from "helpers/hooks/use-debounced-effect";
import { getBillingProviderName } from "helpers/subscriptions";
import { convertTimestampToDate } from "helpers/time";
import { UrlParamTypes } from "types/org-display";

interface ConvertTrialButtonProps {
  refreshOrgData: () => void;
}

const ConvertTrialButton = ({ refreshOrgData }: ConvertTrialButtonProps) => {
  const theme = useTheme();

  const warnContainerSx = {
    background: "#ffd4d4",
    border: "1px solid #941c1c",
    color: "#941c1c",
    borderRadius: 1,
    padding: theme.spacing(1.25),
    margin: "2rem 0.5rem 0 0.5rem",
  };

  const [showLoadingSpinner, setShowLoadingSpinner] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponDescription, setCouponDescription] = useState<Coupon>();

  const { roleId, regionCode } = useParams<UrlParamTypes>();
  const { orgData } = useContext(OrgDataContext);
  const { hasPermissions } = useContext(PermissionsContext);

  const subscription = orgData.subscription[0];
  const billingProviderName = getBillingProviderName(subscription);
  const isOnChargify =
    (subscription?.chargify?.id && subscription?.chargify?.id?.length > 0) ||
    false;

  const canApplyCoupon = hasPermissions([
    ActionType.APPLY_COUPON,
    ActionType.VALIDATE_COUPON,
  ]);

  // Validate coupon code with debounce
  useDebouncedEffect(
    () => {
      if (couponCode.length > 0) {
        validateCoupon(
          {
            code: couponCode,
            provider: subscription.chargify?.site || "",
          },
          callOptionsHeaders(regionCode, orgData.id),
        )
          .then((res) => {
            setCouponDescription(res.coupon);
          })
          .catch(() => {
            setCouponDescription(undefined);
          });
      } else {
        setCouponDescription(undefined);
      }
    },
    1000,
    [couponCode],
  );

  const handleCloseSnackbar = (
    event?: Event | React.SyntheticEvent<unknown, Event>,
    reason?: string,
  ) => {
    if (reason === EventTypeReason.Clickaway) {
      return;
    }
    setOpenSuccessAlert(false);
    setErrorMessage(null);
  };

  const handleConvertTrial = () => {
    setShowLoadingSpinner(true);
    convertTrial(
      {
        organizationId: roleId,
        userId: orgData.members[0],
        couponCode: isApplyingCoupon && couponCode ? couponCode : undefined,
      },
      callOptionsHeaders(regionCode, orgData.id),
    )
      .then(() => {
        setOpenConfirmDialog(false);
        setOpenSuccessAlert(true);
        refreshOrgData();
      })
      .catch((error) => {
        setErrorMessage("Failed to convert trial: " + error.message);
      })
      .finally(() => {
        setShowLoadingSpinner(false);
      });
  };

  return (
    <>
      <SubscriptionAction
        title={`Convert to ${billingProviderName} trial subscription`}
      >
        <Button
          sx={{ mt: 1, mr: 2 }}
          data-testid="btn-convert-trial"
          variant="contained"
          color="primary"
          fullWidth
          onClick={() => setOpenConfirmDialog(true)}
          disabled={isOnChargify || showLoadingSpinner}
        >
          {showLoadingSpinner ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            `CONVERT TO ${billingProviderName.toUpperCase()} TRIAL`
          )}
        </Button>
      </SubscriptionAction>

      <ConfirmDialog
        title={`Convert to ${billingProviderName} trial subscription`}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
        disabled={(isApplyingCoupon && !couponCode) || showLoadingSpinner}
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={handleConvertTrial}
        onCancel={() => setOpenConfirmDialog(false)}
      >
        <Grid container direction="column" spacing={2}>
          <Box width={1} sx={warnContainerSx}>
            <Typography>
              This is a decoupled trial. This means that the trial subscription
              does not exist in {billingProviderName}.
            </Typography>
            <br />
            <Typography>
              Clicking the convert button will create the Trial subscription in
              {billingProviderName} and unlock other subscription-related
              Periscope functionality.
            </Typography>
            <br />
            <Typography>
              Please contact #business-systems for any enquiries.
            </Typography>
          </Box>
          <Grid item>
            <Typography>
              Are you sure you want to convert the decoupled trial subscription
              to a {billingProviderName} trial subscription?
            </Typography>
          </Grid>
          {canApplyCoupon && (
            <Grid item>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isApplyingCoupon}
                    onChange={(e) => setIsApplyingCoupon(e.target.checked)}
                    data-testid="convert-trial-apply-coupon-checkbox"
                  />
                }
                label="Apply coupon code"
                labelPlacement="end"
              />
            </Grid>
          )}
          {canApplyCoupon && isApplyingCoupon && (
            <Grid item>
              <TextField
                required
                name="couponCode"
                label="Coupon code"
                inputProps={{
                  "data-testid": "convert-trial-coupon-field",
                }}
                fullWidth
                margin="normal"
                variant="outlined"
                onChange={(e) => setCouponCode(e.target.value)}
                value={couponCode}
                error={isApplyingCoupon && !couponCode}
              />
              {couponDescription && couponDescription.discountPercentage && (
                <Typography variant="body2" color="success.main">
                  {couponDescription.discountPercentage.toPrecision(4)}%
                  discount
                  {couponDescription.endDate &&
                    ` (expires ${convertTimestampToDate(
                      couponDescription.endDate,
                    )})`}
                </Typography>
              )}
            </Grid>
          )}
          {showLoadingSpinner && (
            <Grid container justifyContent="center" sx={{ mt: 1, mr: 2 }}>
              <CircularProgress
                color="primary"
                variant="indeterminate"
                thickness={4}
                size={30}
              />
            </Grid>
          )}
        </Grid>
      </ConfirmDialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={openSuccessAlert || !!errorMessage}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={handleCloseSnackbar}
        data-testid="snackbar-convert-trial"
      >
        {errorMessage ? (
          <Alert severity="error" variant="filled">
            {errorMessage}
          </Alert>
        ) : (
          <Alert severity="success" variant="filled">
            Trial subscription created in {billingProviderName}
          </Alert>
        )}
      </Snackbar>
    </>
  );
};

export default ConvertTrialButton;
